import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Code,
  Settings,
  DollarSign,
  Calendar,
  Users,
  CheckSquare,
  Plus,
  Star,
  Shield,
  TrendingUp
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import technologyStackMasterService from '../../services/technologyStackMasterService'
import RevisionModal from '../../components/RevisionModal/RevisionModal'
import '../../components/RevisionModal/RevisionModal.css'

const TechnologyStackMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [formData, setFormData] = useState({
    technologyStackCode: '',
    technologyStackName: '',
    technologyStackDescription: '',
    technologyCategory: 'Frontend',
    technologyType: 'Framework',
    technologyStackStatus: 'Active',
    version: '',
    releaseDate: '',
    endOfLifeDate: '',
    compatibleTechnologies: [],
    dependencies: [],
    performanceRating: 5,
    scalabilityRating: 5,
    learningCurve: 'Medium',
    minimumTeamSize: 1,
    recommendedTeamSize: 3,
    skillLevelRequired: 'Mid-level',
    licensingModel: 'Open Source',
    estimatedCost: 0,
    costCurrency: 'USD',
    documentationQuality: 'Good',
    communitySupport: 'Good',
    officialSupport: false,
    marketShare: 0,
    adoptionRate: 'Stable',
    suitableProjectTypes: [],
    suitableProjectSizes: [],
    securityRating: 'Good',
    complianceStandards: []
  })
  const [technologyStackData, setTechnologyStackData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState(null)
  const [activeTab, setActiveTab] = useState('basicInfo')

  useEffect(() => {
    if (location.state?.technologyStackData) {
      const { technologyStackData } = location.state
      setTechnologyStackData(technologyStackData)
      setFormData({
        technologyStackCode: technologyStackData.technologyStackCode || '',
        technologyStackName: technologyStackData.technologyStackName || '',
        technologyStackDescription: technologyStackData.technologyStackDescription || '',
        technologyCategory: technologyStackData.technologyCategory || 'Frontend',
        technologyType: technologyStackData.technologyType || 'Framework',
        technologyStackStatus: technologyStackData.technologyStackStatus || 'Active',
        version: technologyStackData.version || '',
        releaseDate: technologyStackData.releaseDate ? new Date(technologyStackData.releaseDate).toISOString().split('T')[0] : '',
        endOfLifeDate: technologyStackData.endOfLifeDate ? new Date(technologyStackData.endOfLifeDate).toISOString().split('T')[0] : '',
        compatibleTechnologies: technologyStackData.compatibleTechnologies || [],
        dependencies: technologyStackData.dependencies || [],
        performanceRating: technologyStackData.performanceRating || 5,
        scalabilityRating: technologyStackData.scalabilityRating || 5,
        learningCurve: technologyStackData.learningCurve || 'Medium',
        minimumTeamSize: technologyStackData.minimumTeamSize || 1,
        recommendedTeamSize: technologyStackData.recommendedTeamSize || 3,
        skillLevelRequired: technologyStackData.skillLevelRequired || 'Mid-level',
        licensingModel: technologyStackData.licensingModel || 'Open Source',
        estimatedCost: technologyStackData.estimatedCost || 0,
        costCurrency: technologyStackData.costCurrency || 'USD',
        documentationQuality: technologyStackData.documentationQuality || 'Good',
        communitySupport: technologyStackData.communitySupport || 'Good',
        officialSupport: technologyStackData.officialSupport || false,
        marketShare: technologyStackData.marketShare || 0,
        adoptionRate: technologyStackData.adoptionRate || 'Stable',
        suitableProjectTypes: technologyStackData.suitableProjectTypes || [],
        suitableProjectSizes: technologyStackData.suitableProjectSizes || [],
        securityRating: technologyStackData.securityRating || 'Good',
        complianceStandards: technologyStackData.complianceStandards || []
      })
      setLoading(false)
    } else {
      toast.error('No Technology Stack data provided for editing')
      navigate('/projects/technology-stack-master')
    }
  }, [location.state, navigate])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? (parseFloat(value) || 0) : 
              value
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addArrayItem = (field, newItem) => {
    if (newItem.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], newItem.trim()]
      }))
    }
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const addDependency = () => {
    setFormData(prev => ({
      ...prev,
      dependencies: [...prev.dependencies, { name: '', version: '', isRequired: true }]
    }))
  }

  const updateDependency = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.map((dep, i) => 
        i === index ? { ...dep, [field]: value } : dep
      )
    }))
  }

  const removeDependency = (index) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.technologyStackCode.trim()) {
      toast.error('Technology Stack Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.technologyStackCode.trim())) {
      toast.error('Technology Stack Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.technologyStackName.trim()) {
      toast.error('Technology Stack Name is required')
      return false
    }
    if (!formData.technologyStackDescription.trim()) {
      toast.error('Technology Stack Description is required')
      return false
    }
    if (formData.performanceRating < 1 || formData.performanceRating > 10) {
      toast.error('Performance Rating must be between 1 and 10')
      return false
    }
    if (formData.scalabilityRating < 1 || formData.scalabilityRating > 10) {
      toast.error('Scalability Rating must be between 1 and 10')
      return false
    }
    if (formData.marketShare < 0 || formData.marketShare > 100) {
      toast.error('Market Share must be between 0 and 100')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    // Store the form data and show revision modal
    setPendingFormData(formData)
    setShowRevisionModal(true)
  }

  const handleRevisionSave = async (revisionInfo) => {
    try {
      toast.info('Updating Technology Stack Master record...')
      const response = await technologyStackMasterService.updateTechnologyStackMaster(technologyStackData.id, {
        technologyStackCode: pendingFormData.technologyStackCode.toUpperCase(),
        technologyStackName: pendingFormData.technologyStackName,
        technologyStackDescription: pendingFormData.technologyStackDescription,
        technologyCategory: pendingFormData.technologyCategory,
        technologyType: pendingFormData.technologyType,
        technologyStackStatus: pendingFormData.technologyStackStatus,
        version: pendingFormData.version,
        releaseDate: pendingFormData.releaseDate || null,
        endOfLifeDate: pendingFormData.endOfLifeDate || null,
        compatibleTechnologies: pendingFormData.compatibleTechnologies,
        dependencies: pendingFormData.dependencies,
        performanceRating: pendingFormData.performanceRating,
        scalabilityRating: pendingFormData.scalabilityRating,
        learningCurve: pendingFormData.learningCurve,
        minimumTeamSize: pendingFormData.minimumTeamSize,
        recommendedTeamSize: pendingFormData.recommendedTeamSize,
        skillLevelRequired: pendingFormData.skillLevelRequired,
        licensingModel: pendingFormData.licensingModel,
        estimatedCost: pendingFormData.estimatedCost,
        costCurrency: pendingFormData.costCurrency,
        documentationQuality: pendingFormData.documentationQuality,
        communitySupport: pendingFormData.communitySupport,
        officialSupport: pendingFormData.officialSupport,
        marketShare: pendingFormData.marketShare,
        adoptionRate: pendingFormData.adoptionRate,
        suitableProjectTypes: pendingFormData.suitableProjectTypes,
        suitableProjectSizes: pendingFormData.suitableProjectSizes,
        securityRating: pendingFormData.securityRating,
        complianceStandards: pendingFormData.complianceStandards,
        changeReason: revisionInfo.changeReason
      })

      if (response.success) {
        toast.success('Technology Stack Master record updated successfully!', { autoClose: 2000 })
        navigate('/projects/technology-stack-master')
      } else {
        toast.error(response.message || 'Failed to update Technology Stack Master record')
      }
    } catch (error) {
      console.error('Error updating Technology Stack Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Technology Stack Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else if (error.response?.status === 404) {
        toast.error('Technology Stack Master record not found')
      } else {
        toast.error('Failed to update Technology Stack Master record. Please try again.')
      }
    }
  }

  const handleReset = () => {
    if (technologyStackData) {
      setFormData({
        technologyStackCode: technologyStackData.technologyStackCode || '',
        technologyStackName: technologyStackData.technologyStackName || '',
        technologyStackDescription: technologyStackData.technologyStackDescription || '',
        technologyCategory: technologyStackData.technologyCategory || 'Frontend',
        technologyType: technologyStackData.technologyType || 'Framework',
        technologyStackStatus: technologyStackData.technologyStackStatus || 'Active',
        version: technologyStackData.version || '',
        releaseDate: technologyStackData.releaseDate ? new Date(technologyStackData.releaseDate).toISOString().split('T')[0] : '',
        endOfLifeDate: technologyStackData.endOfLifeDate ? new Date(technologyStackData.endOfLifeDate).toISOString().split('T')[0] : '',
        compatibleTechnologies: technologyStackData.compatibleTechnologies || [],
        dependencies: technologyStackData.dependencies || [],
        performanceRating: technologyStackData.performanceRating || 5,
        scalabilityRating: technologyStackData.scalabilityRating || 5,
        learningCurve: technologyStackData.learningCurve || 'Medium',
        minimumTeamSize: technologyStackData.minimumTeamSize || 1,
        recommendedTeamSize: technologyStackData.recommendedTeamSize || 3,
        skillLevelRequired: technologyStackData.skillLevelRequired || 'Mid-level',
        licensingModel: technologyStackData.licensingModel || 'Open Source',
        estimatedCost: technologyStackData.estimatedCost || 0,
        costCurrency: technologyStackData.costCurrency || 'USD',
        documentationQuality: technologyStackData.documentationQuality || 'Good',
        communitySupport: technologyStackData.communitySupport || 'Good',
        officialSupport: technologyStackData.officialSupport || false,
        marketShare: technologyStackData.marketShare || 0,
        adoptionRate: technologyStackData.adoptionRate || 'Stable',
        suitableProjectTypes: technologyStackData.suitableProjectTypes || [],
        suitableProjectSizes: technologyStackData.suitableProjectSizes || [],
        securityRating: technologyStackData.securityRating || 'Good',
        complianceStandards: technologyStackData.complianceStandards || []
      })
      toast.info('Form reset to original values')
    }
  }

  const handleBack = () => {
    navigate('/projects/technology-stack-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Technology Stack Master', path: '/projects/technology-stack-master', onClick: () => navigate('/projects/technology-stack-master') },
    { label: 'Edit', path: `/projects/technology-stack-master/edit/${technologyStackData?.id}`, isCurrent: true }
  ]

  const categoryOptions = [
    'Frontend', 'Backend', 'Database', 'DevOps', 'Mobile', 'Cloud', 'AI/ML', 'Other'
  ]

  const typeOptions = [
    'Framework', 'Library', 'Language', 'Database', 'Tool', 'Platform', 'Service', 'Other'
  ]

  const statusOptions = [
    'Active', 'Inactive', 'Deprecated', 'Legacy'
  ]

  const learningCurveOptions = [
    'Easy', 'Medium', 'Hard', 'Expert'
  ]

  const skillLevelOptions = [
    'Junior', 'Mid-level', 'Senior', 'Expert'
  ]

  const licensingOptions = [
    'Open Source', 'Commercial', 'Freemium', 'Enterprise', 'Free'
  ]

  const qualityOptions = [
    'Excellent', 'Good', 'Fair', 'Poor'
  ]

  const adoptionRateOptions = [
    'Growing', 'Stable', 'Declining', 'New'
  ]

  const projectTypeOptions = [
    'Web Development', 'Mobile Development', 'Desktop Application', 'API Development', 
    'Data Science', 'AI/ML', 'DevOps', 'Cloud Migration', 'Legacy Modernization'
  ]

  const projectSizeOptions = [
    'Small', 'Medium', 'Large', 'Enterprise'
  ]

  const currencyOptions = [
    'USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'
  ]

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Technology Stack Master data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Technology Stack Master Entry</h1>
          <p>Update technology stack information and track changes</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Technology Stack Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <Code size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'specifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('specifications')}
          >
            <Settings size={18} />
            <span>Specifications</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <Star size={18} />
            <span>Performance & Ratings</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'cost' ? 'active' : ''}`}
            onClick={() => setActiveTab('cost')}
          >
            <DollarSign size={18} />
            <span>Cost & Licensing</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'compatibility' ? 'active' : ''}`}
            onClick={() => setActiveTab('compatibility')}
          >
            <CheckSquare size={18} />
            <span>Compatibility & Usage</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Technology Stack Code, Name, Category, Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="technologyStackCode" className="form-label required">
                    Technology Stack Code
                  </label>
                  <input
                    id="technologyStackCode"
                    name="technologyStackCode"
                    type="text"
                    value={formData.technologyStackCode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="e.g., REACT, NODEJS, MONGODB"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="technologyStackName" className="form-label required">
                    Technology Stack Name
                  </label>
                  <input
                    id="technologyStackName"
                    name="technologyStackName"
                    type="text"
                    value={formData.technologyStackName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="e.g., React, Node.js, MongoDB"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="technologyCategory" className="form-label required">
                    Technology Category
                  </label>
                  <select
                    id="technologyCategory"
                    name="technologyCategory"
                    value={formData.technologyCategory}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {categoryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="technologyType" className="form-label required">
                    Technology Type
                  </label>
                  <select
                    id="technologyType"
                    name="technologyType"
                    value={formData.technologyType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {typeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Status, Version, Release Date, End of Life Date */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="technologyStackStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="technologyStackStatus"
                    name="technologyStackStatus"
                    value={formData.technologyStackStatus}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {statusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="version" className="form-label">
                    Version
                  </label>
                  <input
                    id="version"
                    name="version"
                    type="text"
                    value={formData.version}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="e.g., 18.2.0, 3.11.0"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="releaseDate" className="form-label">
                    Release Date
                  </label>
                  <input
                    id="releaseDate"
                    name="releaseDate"
                    type="date"
                    value={formData.releaseDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="endOfLifeDate" className="form-label">
                    End of Life Date
                  </label>
                  <input
                    id="endOfLifeDate"
                    name="endOfLifeDate"
                    type="date"
                    value={formData.endOfLifeDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 3: Technology Stack Description */}
              <div className="form-group">
                <label htmlFor="technologyStackDescription" className="form-label required">
                  Technology Stack Description
                </label>
                <textarea
                  id="technologyStackDescription"
                  name="technologyStackDescription"
                  value={formData.technologyStackDescription}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Describe the technology stack, its purpose, and key features..."
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>
          )}

          {/* Specifications Tab */}
          {activeTab === 'specifications' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Technical Specifications</h3>
              </div>
              
              {/* Row 1: Learning Curve, Skill Level, Team Sizes */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="learningCurve" className="form-label">
                    Learning Curve
                  </label>
                  <select
                    id="learningCurve"
                    name="learningCurve"
                    value={formData.learningCurve}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {learningCurveOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="skillLevelRequired" className="form-label">
                    Skill Level Required
                  </label>
                  <select
                    id="skillLevelRequired"
                    name="skillLevelRequired"
                    value={formData.skillLevelRequired}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {skillLevelOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="minimumTeamSize" className="form-label">
                    Minimum Team Size
                  </label>
                  <input
                    id="minimumTeamSize"
                    name="minimumTeamSize"
                    type="number"
                    value={formData.minimumTeamSize}
                    onChange={handleInputChange}
                    className="form-input"
                    min="1"
                    max="50"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="recommendedTeamSize" className="form-label">
                    Recommended Team Size
                  </label>
                  <input
                    id="recommendedTeamSize"
                    name="recommendedTeamSize"
                    type="number"
                    value={formData.recommendedTeamSize}
                    onChange={handleInputChange}
                    className="form-input"
                    min="1"
                    max="50"
                  />
                </div>
              </div>

              {/* Compatible Technologies */}
              <div className="form-group">
                <label className="form-label">Compatible Technologies</label>
                <div className="array-input-container">
                  {formData.compatibleTechnologies.map((tech, index) => (
                    <div key={index} className="array-input-item">
                      <input
                        type="text"
                        value={tech}
                        onChange={(e) => {
                          const newTechs = [...formData.compatibleTechnologies]
                          newTechs[index] = e.target.value
                          handleArrayChange('compatibleTechnologies', newTechs)
                        }}
                        className="form-input"
                        placeholder="Enter compatible technology"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('compatibleTechnologies', index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('compatibleTechnologies', '')}
                    className="btn-secondary"
                  >
                    <Plus size={16} />
                    Add Technology
                  </button>
                </div>
              </div>

              {/* Dependencies */}
              <div className="form-group">
                <label className="form-label">Dependencies</label>
                <div className="dependencies-container">
                  {formData.dependencies.map((dep, index) => (
                    <div key={index} className="dependency-item">
                      <input
                        type="text"
                        value={dep.name}
                        onChange={(e) => updateDependency(index, 'name', e.target.value)}
                        className="form-input"
                        placeholder="Dependency name"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <input
                        type="text"
                        value={dep.version}
                        onChange={(e) => updateDependency(index, 'version', e.target.value)}
                        className="form-input"
                        placeholder="Version"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={dep.isRequired}
                          onChange={(e) => updateDependency(index, 'isRequired', e.target.checked)}
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        onClick={() => removeDependency(index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addDependency}
                    className="btn-secondary"
                  >
                    <Plus size={16} />
                    Add Dependency
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Performance & Ratings Tab */}
          {activeTab === 'performance' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Performance & Quality Ratings</h3>
              </div>
              
              {/* Row 1: Performance Rating, Scalability Rating, Market Share */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="performanceRating" className="form-label">
                    Performance Rating (1-10)
                  </label>
                  <input
                    id="performanceRating"
                    name="performanceRating"
                    type="number"
                    value={formData.performanceRating}
                    onChange={handleInputChange}
                    className="form-input"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="scalabilityRating" className="form-label">
                    Scalability Rating (1-10)
                  </label>
                  <input
                    id="scalabilityRating"
                    name="scalabilityRating"
                    type="number"
                    value={formData.scalabilityRating}
                    onChange={handleInputChange}
                    className="form-input"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="marketShare" className="form-label">
                    Market Share (%)
                  </label>
                  <input
                    id="marketShare"
                    name="marketShare"
                    type="number"
                    value={formData.marketShare}
                    onChange={handleInputChange}
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              </div>

              {/* Row 2: Documentation Quality, Community Support, Security Rating */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="documentationQuality" className="form-label">
                    Documentation Quality
                  </label>
                  <select
                    id="documentationQuality"
                    name="documentationQuality"
                    value={formData.documentationQuality}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {qualityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="communitySupport" className="form-label">
                    Community Support
                  </label>
                  <select
                    id="communitySupport"
                    name="communitySupport"
                    value={formData.communitySupport}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {qualityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="securityRating" className="form-label">
                    Security Rating
                  </label>
                  <select
                    id="securityRating"
                    name="securityRating"
                    value={formData.securityRating}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {qualityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Adoption Rate, Official Support */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="adoptionRate" className="form-label">
                    Adoption Rate
                  </label>
                  <select
                    id="adoptionRate"
                    name="adoptionRate"
                    value={formData.adoptionRate}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {adoptionRateOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="officialSupport"
                      checked={formData.officialSupport}
                      onChange={handleInputChange}
                      style={{ margin: 0 }}
                    />
                    <span>Official Support Available</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Cost & Licensing Tab */}
          {activeTab === 'cost' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Cost & Licensing Information</h3>
              </div>
              
              {/* Row 1: Licensing Model, Estimated Cost, Currency */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="licensingModel" className="form-label">
                    Licensing Model
                  </label>
                  <select
                    id="licensingModel"
                    name="licensingModel"
                    value={formData.licensingModel}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {licensingOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="estimatedCost" className="form-label">
                    Estimated Cost
                  </label>
                  <input
                    id="estimatedCost"
                    name="estimatedCost"
                    type="number"
                    value={formData.estimatedCost}
                    onChange={handleInputChange}
                    className="form-input"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="costCurrency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="costCurrency"
                    name="costCurrency"
                    value={formData.costCurrency}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {currencyOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Compatibility & Usage Tab */}
          {activeTab === 'compatibility' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Compatibility & Usage Information</h3>
              </div>
              
              {/* Suitable Project Types */}
              <div className="form-group">
                <label className="form-label">Suitable Project Types</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projectTypeOptions.map(option => (
                    <label key={option} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.suitableProjectTypes.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('suitableProjectTypes', option)
                          } else {
                            removeArrayItem('suitableProjectTypes', formData.suitableProjectTypes.indexOf(option))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Suitable Project Sizes */}
              <div className="form-group">
                <label className="form-label">Suitable Project Sizes</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projectSizeOptions.map(option => (
                    <label key={option} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.suitableProjectSizes.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('suitableProjectSizes', option)
                          } else {
                            removeArrayItem('suitableProjectSizes', formData.suitableProjectSizes.indexOf(option))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Compliance Standards */}
              <div className="form-group">
                <label className="form-label">Compliance Standards</label>
                <div className="array-input-container">
                  {formData.complianceStandards.map((standard, index) => (
                    <div key={index} className="array-input-item">
                      <input
                        type="text"
                        value={standard}
                        onChange={(e) => {
                          const newStandards = [...formData.complianceStandards]
                          newStandards[index] = e.target.value
                          handleArrayChange('complianceStandards', newStandards)
                        }}
                        className="form-input"
                        placeholder="Enter compliance standard (e.g., GDPR, SOC 2)"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('complianceStandards', index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('complianceStandards', '')}
                    className="btn-secondary"
                  >
                    <Plus size={16} />
                    Add Standard
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleReset}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            
            <button 
              type="button" 
              className="btn-primary" 
              onClick={handleSave}
            >
              <Save size={18} />
              Update Technology Stack
            </button>
          </div>
        </form>
      </div>

      {/* Revision Modal */}
      <RevisionModal
        isOpen={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        onSave={handleRevisionSave}
        currentRevision={technologyStackData?.currentRevision || 1}
      />
    </div>
  )
}

export default TechnologyStackMasterEdit
